import React, { useEffect, useState, useRef } from "react";

import ConsentComponent from "../component/app/verification/Consent.component";
import InsuranceProviderComponent from "../component/app/verification/InsuranceProvider.component";
import ProviderLoginComponent from "../component/app/verification/ProviderLogin.component";
import DocumentPull from "../component/app/verification/DocumentPull.component";
import TradeVehicleComponent from "../component/app/verification/TradeVehicle.component";
import RequestQuoteComponent from "../component/app/verification/RequestQuote.component";
import ThankYouComponent from "../component/app/verification/ThankYou.component";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import { ON_INSURANCE_WEBHOOK } from "../graphql/subscription/subscription";
import { useVerification } from "../context/Verification.context";
import DialogComponent from "../component/base/Dialog.component";
import { useAppContext } from "../context/App.context";
import InformationDialog from "../component/base/dialog-body/Information.dialog";
import { TYPE, closeWindow } from "../utils/Utils";
import { REQUEST_QUOTE } from "../graphql/mutation/mutation";
import {
  FETCH_ARTIFACTS,
  POLL_CANOPY_STATUS,
  PULL_INSURANCE,
} from "../graphql/query/query";
import { useNavigate } from "react-router-dom";
import KnowledgeHub from "./KnowledgeHub.page";
import { Paper, Typography, Button, Box } from "@mui/material";
import {
  navigateContinueDelivery,
  includesKnowledgeHubService,
  getPostPullNavTarget,
  getContinueDeliveryNavTarget,
} from "../utils/videoPlayer";

const isDev = process.env.NODE_ENV === "development";
const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const DIALOG_INITIAL_STATE = {
  open: false,
  bodyType: TYPE.DEFAULT,
  title: "",
  body: null,
};

const VerificationPage = ({ route }) => {
  const verification = useVerification();
  const app = useAppContext();
  const navigate = useNavigate();

  const [errorNavigation, setErrorNavigation] = useState(null);
  const [errorResetTransaction, setErrorResetTransaction] = useState(true);
  const [status, setStatus] = useState("PROCESSING");
  const [eventType, setEventType] = useState(null);
  const [dialog, setDialog] = useState(DIALOG_INITIAL_STATE);
  const [isCalled, setCalled] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [isPullComplete, setISPullComplete] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [criticalMfa, setCriticalMfa] = useState(false);
  const [showLogDialog, setShowLogDialog] = useState(false);

  const logsRef = useRef([]);
  const pullCalledRef = useRef(false);

  const pushDebugLog = (logsRef, label, data = "") => {
    if (!logsRef || !logsRef.current) return;
    const time = new Date().toISOString().split("T")[1].split(".")[0];
    const msg = typeof data === "object" ? JSON.stringify(data) : String(data);
    logsRef.current.unshift(`[${time}] ${label} ${msg}`);
    if (logsRef.current.length > 200) logsRef.current.pop();
  };

  const interceptConsole = (logsRef) => {
    const methods = ["log", "info", "error"];
    methods.forEach((method) => {
      const originalMethod = console[method];
      console[method] = (...args) => {
        const message = args.map((arg) => (typeof arg === "object" ? JSON.stringify(arg) : arg)).join(" ");
        pushDebugLog(logsRef, `[CONSOLE-${method.toUpperCase()}]`, message);
        originalMethod.apply(console, args);
      };
    });
  };

  useEffect(() => {
    if (logsRef.current) interceptConsole(logsRef);
  }, [logsRef]);

  const { data: canopyStatus, stopPolling, startPolling } = useQuery(POLL_CANOPY_STATUS, {
    variables: { pull_id: verification?.pullInsurance?.pullId },
    pollInterval: 2000,
    skip: !verification?.pullInsurance?.pullId || (route !== "verifyInsurance" && route !== "providerLogin"),
  });

  const [requestQuote] = useMutation(REQUEST_QUOTE);

  const { data: fetchartifacts, refetch: artifactRefetch, loading: artifactsLoading } = useQuery(FETCH_ARTIFACTS, {
    variables: { fetchInput: { dealershipId: parseInt(verification.dealershipId) } },
  });

  useEffect(() => {
    if (fetchartifacts?.fetchArtifacts) {
      if (!fetchartifacts?.fetchArtifacts?.isArtifact) {
        verification.setArtifacts([]);
        verification.setAIData((prev) => ({
          ...prev,
          spokeperson: false,
          spanish: false,
          isMuted: true,
          isLoading: false,
          isAI: false,
          isOther: false,
        }));
      } else {
        const tradeArtifact = fetchartifacts?.fetchArtifacts?.tradeArtifact;
        const quoteArtifact = fetchartifacts?.fetchArtifacts?.quoteArtifact;
        const surveyOther = fetchartifacts?.fetchArtifacts?.surveyOther;

        const englishText = surveyOther?.find((item) => item?.language_id === 1);
        const spanishText = surveyOther?.find((item) => item?.language_id === 2);

        const combineOther = {
          text: englishText?.text,
          es_text: spanishText?.text,
          ai_url: { english_url: englishText?.url, spanish_url: spanishText?.url },
          typeName: "surveyOther",
        };

        if (tradeArtifact || quoteArtifact) {
          const combinedArtifacts = [
            ...(tradeArtifact ? [tradeArtifact] : []),
            ...(quoteArtifact ? [quoteArtifact] : []),
            ...(combineOther ? [combineOther] : []),
          ];
          verification.setTradeQuote(combinedArtifacts);
        }

        const data = fetchartifacts?.fetchArtifacts?.review;
        let getReview = [];
        let getReferral = [];

        if (data) {
          const reviewData = {
            dealer_rater: data?.dealer_rater,
            google: data?.google,
            facebook: data?.facebook,
            es_message: data?.es_message,
            ai_url: data?.rating_ai_url,
            id: data?.id,
            message: data?.message,
            position: data?.position,
            __typename: "ReviewUrl",
          };
          if (reviewData.message || reviewData?.es_message) getReview.push(reviewData);

          const referralData = {
            id: data?.id,
            referral_question: data?.referral_question,
            es_referral_question: data?.es_referral_question,
            review_question: data?.review_question,
            es_review_question: data?.es_review_question,
            ai_url: data?.review_ai_url,
            referral_ai_url: data?.referral_ai_url,
            position: data?.position,
          };
          if (referralData.id) getReferral.push(referralData);
        }

        const textSurveyArtifacts = [
          ...(fetchartifacts?.fetchArtifacts.text
            ?.filter((item) => item.position)
            .map((item) => ({ ...item, artifact_type_id: 1 })) || []),
          ...(fetchartifacts?.fetchArtifacts.survey
            ?.filter((item) => item.position)
            .map((item) => ({ ...item, artifact_type_id: 2 })) || []),
          ...(fetchartifacts?.fetchArtifacts.video
            ?.filter((item) => item.position)
            .map((item) => ({ ...item, artifact_type_id: 3 })) || []),
          ...(fetchartifacts?.fetchArtifacts.vehicleTransactionArtifact
            ?.filter((item) => item.position)
            .map((item) => ({ ...item, artifact_type_id: 17 })) || []),
          // Warranty Avatar video — included in the sequence at the admin-configured position
          ...(fetchartifacts?.fetchArtifacts?.warrantyArtifact &&
          fetchartifacts.fetchArtifacts.warrantyArtifact.position
            ? [{ ...fetchartifacts.fetchArtifacts.warrantyArtifact, artifact_type_id: 18 }]
            : []),
        ];

        const array = [
          ...textSurveyArtifacts,
          ...(fetchartifacts?.fetchArtifacts?.dealer_app &&
          fetchartifacts.fetchArtifacts?.dealer_app?.position !== null
            ? [{ ...fetchartifacts?.fetchArtifacts?.dealer_app, artifact_type_id: 5 }]
            : []),
          ...(getReferral[0]?.position ||
          (getReferral?.[0]?.review_question && getReferral[0]?.position)
            ? getReferral.map((item) => ({ ...item, artifact_type_id: 7 }))
            : []),
          ...(getReview.length > 0 && getReview[0]?.position
            ? getReview.map((item) => ({ ...item, artifact_type_id: 7 }))
            : []),
          ...(fetchartifacts?.fetchArtifacts.appointment &&
          fetchartifacts.fetchArtifacts.appointment.position !== null
            ? [
                {
                  ...fetchartifacts.fetchArtifacts.appointment,
                  artifact_type_id: 6,
                },
              ]
            : []),
        ].sort((a, b) => a.position - b.position);

        const aiURLFound =
          array.some((item) => item.ai_url && (item.ai_url?.english_url != "null" || item.ai_url?.spanish_url != "null")) ||
          (fetchartifacts?.fetchArtifacts?.tradeArtifact || fetchartifacts?.fetchArtifacts?.quoteArtifact).ai_url?.english_url != "null" ||
          (fetchartifacts?.fetchArtifacts?.tradeArtifact || fetchartifacts?.fetchArtifacts?.quoteArtifact).ai_url?.spanish_url != "null";

        // FIX: warrantyArtifact (incl. is_enabled) was previously never copied
        // into the context object — isWarrantyModuleEnabled downstream always
        // read undefined for warrantyArtifact.is_enabled, so the warranty
        // popup/step never showed even when the admin toggle was ON.
        verification.setArtifacts({
          items: array,
          vehicleTransactionArtifact: fetchartifacts?.fetchArtifacts?.vehicleTransactionArtifact || [],
          displayRules: fetchartifacts?.fetchArtifacts?.displayRules || [],
          is_enabled: fetchartifacts?.fetchArtifacts?.is_enabled || false,
          has_warranty_coverages:
            fetchartifacts?.fetchArtifacts?.has_warranty_coverages || false,
          warrantyArtifact: fetchartifacts?.fetchArtifacts?.warrantyArtifact || null,
        });
        verification.setAIData((prev) => ({
          ...prev,
          spokeperson: aiURLFound,
          spanish: false,
          isMuted: true,
          isLoading: false,
          isAI: aiURLFound,
          isOther: false,
        }));
      }
    }
  }, [fetchartifacts]);

  useEffect(() => {
    const artifactItems = Array.isArray(verification?.artifacts)
      ? verification?.artifacts
      : verification?.artifacts?.items || [];
    // Dashboard may briefly have serviceId ["1"] before consent resolves
    // dealership KH — still allow artifacts when KH is enabled (not URL insurance-only).
    const canShowKhArtifacts =
      artifactItems.length > 0 && includesKnowledgeHubService(verification);

    if (!canShowKhArtifacts) {
      verification.setIsArtifactOpen(true);
      verification.setAIData((prev) => ({
        ...prev,
        spokeperson: true,
        spanish: false,
        isMuted: false,
        isLoading: false,
        isOther: false,
      }));
    } else {
      verification.setIsArtifactOpen(false);
    }
  }, [verification?.artifacts, verification?.checkService, verification?.customer?.serviceId]);

  useQuery(PULL_INSURANCE, {
    fetchPolicy: "network-only",
    skip: (status !== "SUCCESS" && status !== "COMPLETE") || (eventType !== "COMPLETE") || pullCalledRef.current,
    variables: {
      pullInput: {
        dealership_id: parseInt(verification?.dealershipId),
        employee_user_id: parseInt(verification?.employeeId),
        teamId: "71c9a296-3c62-4438-82bb-5c57ff6a435d",
        pullId: verification?.pullInsurance?.pullId,
        pullJWT: verification?.pullInsurance?.pullJwt,
        vehicle_id: verification?.customer?.vehicleId,
        customer: {
          user_id: verification?.customer?.userId,
          first_name: verification?.customer?.firstName,
          last_name: verification?.customer?.lastName,
          email: verification?.customer?.email,
          mobile_number: verification?.customer?.mobileNumber?.replace(/\D/g, ""),
          vin: verification?.customer?.vin,
        },
        isRepull: verification?.isRepull === "true" || verification?.isRepull === true,
      },
    },
    onCompleted: (data) => {
      pullCalledRef.current = true;
      if (data && data.pullInsurance.errors) {
        if (data.pullInsurance.errors[0].message) {
          setDialog({
            open: true,
            bodyType: TYPE.DEFAULT,
            title: "Error",
            body: <InformationDialog text={data.pullInsurance.errors[0].message} />,
          });
        }
      } else {
        setCalled(true);
        verification.setCustomer({
          ...verification.customer,
          premium: data.pullInsurance.premium,
          isPolicyAvailable: data.pullInsurance.isPolicyAvailable,
          totalPremiumCents: data.pullInsurance.totalPremiumCents,
          claims: data?.pullInsurance?.claims,
          insurance_id: data?.pullInsurance?.insurance_id,
        });

        if (!verification?.isRepull === "true" || !verification?.isRepull === true) {
          requestQuote({
            variables: {
              requestInput: {
                employee_user_id: parseInt(verification?.employeeId),
                user_id: verification?.customer?.userId,
                dealership_id: parseInt(verification?.dealershipId),
                d_startDate: app.fetchData.d_startDate,
                d_endDate: app.fetchData.d_endDate,
                f_startDate: app.fetchData.f_startDate,
                f_endDate: app.fetchData.f_endDate,
                pageCount: app.fetchData.pageCount,
                rowsPerPage: app.fetchData.rowsPerPage,
                search_employee: app.fetchData.searchEmployee,
                search_customer: app.fetchData.searchCustomer,
                isEmployeeOnly: false,
                timezone: new Date().getTimezoneOffset(),
                isRequestQuote: false,
                insuranceCompleted: true,
                insurance_id: data?.pullInsurance?.insurance_id,
                type: "verification",
              },
            },
          });
        }

        const isRepullFlow = verification?.isRepull === "true" || verification?.isRepull === true;
        const getNavTarget = () => {
          if (isRepullFlow || verification?.isFromCustomerTable) return `/verification/thankYou`;
          if (verification.isArtifactOpen) {
            return getPostPullNavTarget(verification);
          }
          return null;
        };

        const navTarget = getNavTarget();
        if (navTarget) {
          // KH WalkMe is on the artifact screen during pull — no dialog deferral
          console.log("navigate to", navTarget);
          navigate(navTarget, { replace: true });
        }

        setISPullComplete(true);
        setIsRunning(false);
      }
    },
    onError: () => setIsRunning(false),
  });

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isRunning) {
        event.preventDefault();
        event.returnValue = "Are you sure you want to leave? Changes may not be saved.";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isRunning]);

  useEffect(() => {
    const onInsurancePull = canopyStatus?.pollCanopyStatus;
    if (!onInsurancePull) return;

    const _pullStatus = onInsurancePull.status;
    const _eventType = onInsurancePull.event_type;
    const pullData = JSON.parse(onInsurancePull.data) || "{}";

    setStatus(_pullStatus);
    setEventType(_eventType);

    if (pullData?.auth_status === "IDENTITY_VERIFICATION" || pullData?.auth_status === "IDENTITY_VERIFICATION_OPTIONS") {
      if (!criticalMfa) setCriticalMfa(true);
    }

    if (
      route === "verifyInsurance" &&
      ((_eventType === "AUTH_STATUS" && _pullStatus === "NOT_AUTHENTICATED") || 
       (pullData?.login_error_message && _pullStatus !== "IDENTITY_VERIFICATION" && _pullStatus !== "IDENTITY_VERIFICATION_OPTIONS"))
    ) {
      if (verification?.isResending) {
        console.log("Ignoring NOT_AUTHENTICATED error during resend operation");
        return;
      }

      //  Shared Continue Delivery button used in both MFA and credential error dialogs
      const ContinueDeliveryButton = () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
          <Button
            variant="outlined"
            sx={{ borderColor: "#9e9e9e", borderWidth: "2px", width: "200px" }}
            onClick={() => {
              setDialog((prev) => ({ ...prev, open: false }));
              navigateContinueDelivery(
                navigate,
                verification,
                getContinueDeliveryNavTarget(verification),
              );
            }}
          >
            Continue Delivery
          </Button>
        </div>
      );

      if (criticalMfa) {
        setErrorNavigation(`/verification/providerLogin`);
        setErrorResetTransaction(false);
        setDialog({
          open: true,
          bodyType: TYPE.DEFAULT,
          title: "That MFA code did not work",
          body: (
            <InformationDialog
              align="left"
              text="Please double check your MFA code and try again."
              actions={
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Button
                    variant="outlined"
                    sx={{ borderColor: "#9e9e9e", borderWidth: "2px", width: "200px" }}
                    onClick={() => {
                      setDialog((prev) => ({ ...prev, open: false }));
                      navigate("/verification/providerLogin");
                    }}
                  >
                    Try Again
                  </Button>
                </div>
              }
              helperText="Verifying insurance helps finalize your paperwork faster. You can always provide insurance manually if you prefer."
              additionalActions={<ContinueDeliveryButton />} // 👈
            />
          ),
        });
      } else {
        setErrorNavigation(`/verification/providerLogin`);
        setErrorResetTransaction(false);
        setDialog((prevDialog) => ({
          ...prevDialog,
          open: !prevDialog.open,
          title: "We couldn't sign you in yet :",
          body: (
            <InformationDialog
              text="Please double-check your Username & Password and try again."
              actions={
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Button
                    variant="outlined"
                    sx={{ borderColor: "#9e9e9e", borderWidth: "2px", width: "200px" }}
                    onClick={() => {
                      setDialog((prev) => ({ ...prev, open: false }));
                      navigate("/verification/providerLogin");
                    }}
                  >
                    Try Again
                  </Button>
                </div>
              }
              helperText="Verifying insurance helps finalize your paperwork faster. You can always provide insurance manually if you prefer."
              additionalActions={<ContinueDeliveryButton />} // 👈
            />
          ),
        }));
      }
      setCriticalMfa(false);
    } else if (_eventType === "ERROR") {
      console.log("SYSTEM DOWN DIALOG");
      setErrorNavigation(`/verification/providerLogin`, { replace: true });
      setErrorResetTransaction(false);
      setDialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: _eventType,
        body: (
          <Paper sx={{ background: "white", padding: "16px", display: { xs: "contents", sm: "flex" }, justifyContent: "center", alignItems: "center", boxShadow: "none", flexDirection: "column" }}>
            <Typography variant="h6" sx={{ color: "text.disabled" }}>Insurance provider temporarily down. Please try again later</Typography>
            <Typography variant="h6" sx={{ color: "text.disabled" }}>{_pullStatus}</Typography>
          </Paper>
        ),
      });
      setCriticalMfa(false);
    } else if (_eventType === "COMPLETE") {
      stopPolling();
    }
  }, [canopyStatus]);

  const Component = () => {
    switch (route) {
      case "consent": return <ConsentComponent />;
      case "selectProvider": return <InsuranceProviderComponent />;
      case "providerLogin": return <ProviderLoginComponent canopyPollStatus={canopyStatus?.pollCanopyStatus} />;
      case "verifyInsurance":
        return (
          <DocumentPull
            onInsurancePull={canopyStatus?.pollCanopyStatus}
            isPullComplete={isPullComplete}
            setISPullComplete={setISPullComplete}
            status={status}
            dialogOpen={dialog.open}
            stopPolling={stopPolling}
            startPolling={startPolling}
          />
        );
      case "tradeVehicle": return <TradeVehicleComponent />;
      case "requestQuote": return <RequestQuoteComponent />;
      case "thankYou":
        return (
          <ThankYouComponent
            skipped={skipped}
            setSkipped={setSkipped}
            artifactRefetch={artifactRefetch}
            artifactsLoading={artifactsLoading}
          />
        );
      case "knowledgeHub": return <KnowledgeHub />;
      default: return null;
    }
  };

  return (
    <>
      {Component(route)}

      {isDev && isMobile && (
        <Button
          size="small"
          variant="contained"
          sx={{ position: "fixed", bottom: 12, right: 12, zIndex: 9999, minWidth: "40px", padding: "4px" }}
          onClick={() => setShowLogDialog(true)}
        >
          click
        </Button>
      )}

      <DialogComponent
        open={showLogDialog}
        title="Debug Logs"
        bodyType={TYPE.DEFAULT}
        body={
          <Box sx={{ maxHeight: "60vh", overflowY: "auto", fontSize: "12px", whiteSpace: "pre-wrap" }}>
            {logsRef?.current?.join("\n")}
          </Box>
        }
        onClose={() => setShowLogDialog(false)}
      />

      <DialogComponent
        open={dialog.open}
        bodyType={dialog.bodyType}
        body={dialog.body}
        title={dialog.title}
        onClose={() => {
          setDialog({ ...dialog, open: false });
          if (errorNavigation !== null && route !== "verifyInsurance") {
            if (errorResetTransaction) {
              verification.setCustomer({ ...verification.customer, transactionId: Date.now().toString() });
            }
            navigate(errorNavigation);
            setErrorNavigation(null);
            setErrorResetTransaction(true);
          } else if (errorNavigation !== null && route === "verifyInsurance") {
            if (errorResetTransaction) {
              verification.setCustomer({ ...verification.customer, transactionId: Date.now().toString() });
            }
            navigate(errorNavigation);
            setErrorNavigation(null);
            setErrorResetTransaction(true);
          }
        }}
      />
    </>
  );
};

export default VerificationPage;